import type { FC, ReactNode } from 'react'
import type { ClockChange, ClockRegion } from '@/lib/circadian-planner'

import { useEffect, useState } from 'react'
import Link from 'next/link'

import {
    AlarmClock,
    Info,
    Moon,
    Sun,
    Sunrise,
    Sunset
} from 'lucide-react'

import { cn } from '@/lib/utils'

import {
    CLOCK_CHANGE_MIN,
    CLOCK_EFFECT_DAYS,
    CLOCK_NIGHT_OPTIONS,
    CLOCK_REGIONS,
    CLOCK_STEP_OPTIONS,
    addDays,
    buildClockChangePlan,
    formatClock,
    formatDay,
    nextChangeDate,
    parseTime,
    soonestChange
} from '@/lib/circadian-planner'

const inputClass =
    'mt-1 w-full rounded-lg border border-border bg-background px-3 py-2 text-base text-foreground outline-none focus-visible:ring-2 focus-visible:ring-ring'

const chipClass = (selected: boolean): string =>
    cn(
        'rounded-full border px-4 py-2 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        selected
            ? 'border-primary bg-primary text-primary-foreground'
            : 'border-border bg-background text-foreground hover:bg-secondary'
    )

const choiceClass = (selected: boolean): string =>
    cn(
        'flex flex-col items-start rounded-xl border px-4 py-3 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        selected
            ? 'border-primary bg-primary text-primary-foreground'
            : 'border-border bg-background text-foreground hover:bg-secondary'
    )

const CHANGE_OPTIONS: { value: ClockChange; label: string; hint: string; icon: typeof Sunrise }[] = [
    { value: 'spring', label: 'Spring forward', hint: 'Clocks go ahead 1 hour', icon: Sunrise },
    { value: 'fall', label: 'Fall back', hint: 'Clocks go back 1 hour', icon: Sunset }
]

export const ClockChangePlanner: FC = (): ReactNode => {
    const [region, setRegion] = useState<ClockRegion>('us')
    const [change, setChange] = useState<ClockChange>('spring')
    const [bed, setBed] = useState('23:00')
    const [wake, setWake] = useState('07:00')
    const [nights, setNights] = useState(3)
    const [step, setStep] = useState(20)
    const [shortOnSleep, setShortOnSleep] = useState(false)
    const [today, setToday] = useState<Date | null>(null)

    // The visitor's date is only known in the browser, so dates appear after mount,
    // with the next change selected by default.
    useEffect(() => {
        const now = new Date()
        const start = new Date(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate()))
        setToday(start)
        setChange(soonestChange('us', start))
    }, [])

    const bedMin = parseTime(bed)
    const wakeMin = parseTime(wake)
    const plan =
        bedMin !== null && wakeMin !== null
            ? buildClockChangePlan({ change, bed: bedMin, wake: wakeMin, nights, stepMin: step, shortOnSleep })
            : null

    const changeDate = today ? nextChangeDate(region, change, today) : null
    const changeNight = changeDate ? addDays(changeDate, -1) : null
    const nightDate = (nightsBefore: number): Date | null => (changeNight ? addDays(changeNight, -nightsBefore) : null)
    const firstNight = plan && plan.nights.length ? nightDate(plan.nights[0].nightsBefore) : null
    const someNightsPassed = Boolean(today && firstNight && firstNight.getTime() < today.getTime())

    const spring = change === 'spring'
    const direction = spring ? 'earlier' : 'later'

    return (
        <section
            aria-label='Clock change planner'
            className='my-10 overflow-hidden rounded-2xl border border-border bg-card shadow-sm'
        >
            <div className='border-b border-border bg-secondary/50 px-5 py-4 sm:px-6'>
                <div className='flex items-center gap-2'>
                    <AlarmClock className='size-5 shrink-0 text-primary' />
                    <span className='font-heading text-lg font-semibold tracking-tight text-foreground'>
                        Clock change planner
                    </span>
                </div>
                <span className='mt-1 block text-sm text-muted-foreground'>
                    Move your sleep a little each night before the clocks change, so the change itself is easier.
                </span>
            </div>

            <div className='px-5 py-5 sm:px-6'>
                <span id='clock-region-label' className='block text-xs font-medium text-muted-foreground'>
                    Where you live
                </span>
                <div role='radiogroup' aria-labelledby='clock-region-label' className='mt-2 flex flex-wrap gap-2'>
                    {CLOCK_REGIONS.map((option) => (
                        <button
                            key={option.value}
                            type='button'
                            role='radio'
                            aria-checked={region === option.value}
                            onClick={() => setRegion(option.value)}
                            className={chipClass(region === option.value)}
                        >
                            {option.label}
                        </button>
                    ))}
                </div>

                <span id='clock-change-label' className='mt-5 block text-xs font-medium text-muted-foreground'>
                    Which change
                </span>
                <div role='radiogroup' aria-labelledby='clock-change-label' className='mt-2 grid grid-cols-1 gap-3 sm:grid-cols-2'>
                    {CHANGE_OPTIONS.map((option) => {
                        const Icon = option.icon
                        const date = today ? nextChangeDate(region, option.value, today) : null
                        const selected = change === option.value

                        return (
                            <button
                                key={option.value}
                                type='button'
                                role='radio'
                                aria-checked={selected}
                                onClick={() => setChange(option.value)}
                                className={choiceClass(selected)}
                            >
                                <span className='flex items-center gap-2 text-sm font-semibold'>
                                    <Icon className='size-4' />
                                    {option.label}
                                </span>
                                <span className={cn('mt-1 text-xs', selected ? 'text-primary-foreground/85' : 'text-muted-foreground')}>
                                    {option.hint}
                                    {date ? `. Next: ${formatDay(date)}` : ''}
                                </span>
                            </button>
                        )
                    })}
                </div>

                <div className='mt-5 grid grid-cols-2 gap-4'>
                    <div>
                        <label htmlFor='clock-bed' className='block text-xs font-medium text-muted-foreground'>
                            Usual bedtime
                        </label>
                        <input id='clock-bed' type='time' value={bed} onChange={(event) => setBed(event.target.value)} className={inputClass} />
                    </div>
                    <div>
                        <label htmlFor='clock-wake' className='block text-xs font-medium text-muted-foreground'>
                            Usual wake time
                        </label>
                        <input id='clock-wake' type='time' value={wake} onChange={(event) => setWake(event.target.value)} className={inputClass} />
                    </div>
                </div>

                <div className='mt-5 grid grid-cols-1 gap-5 sm:grid-cols-2'>
                    <div>
                        <span id='clock-nights-label' className='block text-xs font-medium text-muted-foreground'>
                            Nights to prepare
                        </span>
                        <div role='radiogroup' aria-labelledby='clock-nights-label' className='mt-2 flex flex-wrap gap-2'>
                            {CLOCK_NIGHT_OPTIONS.map((option) => (
                                <button
                                    key={option}
                                    type='button'
                                    role='radio'
                                    aria-checked={nights === option}
                                    onClick={() => setNights(option)}
                                    className={chipClass(nights === option)}
                                >
                                    {option} nights
                                </button>
                            ))}
                        </div>
                    </div>
                    <div>
                        <span id='clock-step-label' className='block text-xs font-medium text-muted-foreground'>
                            Shift each night
                        </span>
                        <div role='radiogroup' aria-labelledby='clock-step-label' className='mt-2 flex flex-wrap gap-2'>
                            {CLOCK_STEP_OPTIONS.map((option) => (
                                <button
                                    key={option}
                                    type='button'
                                    role='radio'
                                    aria-checked={step === option}
                                    onClick={() => setStep(option)}
                                    className={chipClass(step === option)}
                                >
                                    {option} minutes
                                </button>
                            ))}
                        </div>
                    </div>
                </div>

                {!spring && (
                    <label className='mt-5 flex cursor-pointer items-start gap-3 rounded-xl border border-border bg-background px-4 py-3'>
                        <input
                            type='checkbox'
                            checked={shortOnSleep}
                            onChange={(event) => setShortOnSleep(event.target.checked)}
                            className='mt-0.5 size-4 shrink-0 accent-primary'
                        />
                        <span className='text-sm text-foreground'>
                            I have been short on sleep for a while
                            <span className='mt-0.5 block text-xs text-muted-foreground'>
                                Then the extra hour is worth keeping, so the plan skips the later bedtimes.
                            </span>
                        </span>
                    </label>
                )}

                <div aria-live='polite' className='mt-6'>
                    {!plan ? (
                        <span className='block rounded-xl border border-border bg-background px-4 py-4 text-sm text-muted-foreground'>
                            Enter your usual bedtime and wake time to see your plan.
                        </span>
                    ) : (
                        <div>
                            <div className='rounded-xl border border-primary/40 bg-primary/5 px-4 py-4'>
                                <span className='block text-sm font-medium text-foreground'>
                                    {changeDate ? `Clocks ${spring ? 'go ahead' : 'go back'} on ${formatDay(changeDate)}` : `Clocks ${spring ? 'go ahead' : 'go back'} one hour`}
                                </span>
                                <span className='mt-1 block font-heading text-2xl font-semibold tracking-tight text-foreground sm:text-3xl'>
                                    {plan.skipPrep
                                        ? 'Keep your bedtime and take the extra hour'
                                        : `${step} minutes ${direction} each night`}
                                </span>
                                <span className='mt-2 block text-sm leading-relaxed text-muted-foreground'>
                                    {plan.skipPrep
                                        ? 'Go to bed at your usual time on the new clock and the change hands you an hour of extra sleep.'
                                        : `Over ${plan.nights.length} nights you move ${plan.preparedMin} of the ${CLOCK_CHANGE_MIN} minutes before the clocks change.${plan.preparedMin < CLOCK_CHANGE_MIN ? ' The rest catches up in the first few days after.' : ''} Move dinner by the same amount each night.`}
                                </span>
                            </div>

                            {someNightsPassed && (
                                <span className='mt-3 block text-sm text-muted-foreground'>
                                    Some of these nights have already passed. Start from tonight with the step for tonight, or pick fewer nights.
                                </span>
                            )}

                            <span className='mt-5 flex flex-col gap-3'>
                                {plan.nights.map((night) => {
                                    const date = nightDate(night.nightsBefore)

                                    return (
                                        <span key={night.key} className='flex gap-3 rounded-xl border border-border bg-background px-4 py-3'>
                                            <Moon className='mt-0.5 size-4 shrink-0 text-primary' />
                                            <span className='text-sm text-foreground'>
                                                <span className='block font-semibold'>
                                                    {date
                                                        ? `${formatDay(date)} night`
                                                        : `${night.nightsBefore} ${night.nightsBefore === 1 ? 'night' : 'nights'} before the change`}
                                                </span>
                                                <span className='mt-0.5 block text-muted-foreground'>
                                                    Bed at {formatClock(night.bed)}, up at {formatClock(night.wake)}.{' '}
                                                    {night.shiftMin} minutes {direction} than usual.
                                                </span>
                                            </span>
                                        </span>
                                    )
                                })}

                                <span className='flex gap-3 rounded-xl border border-primary/40 bg-background px-4 py-3'>
                                    <AlarmClock className='mt-0.5 size-4 shrink-0 text-primary' />
                                    <span className='text-sm text-foreground'>
                                        <span className='block font-semibold'>
                                            {changeNight ? `${formatDay(changeNight)} night: the change` : 'The night of the change'}
                                        </span>
                                        <span className='mt-0.5 block text-muted-foreground'>
                                            Set your clocks {spring ? 'ahead' : 'back'} an hour in the early evening, then go to bed at
                                            your usual {bedMin !== null ? formatClock(bedMin) : 'bedtime'} on the new time.
                                        </span>
                                    </span>
                                </span>

                                <span className='flex gap-3 rounded-xl border border-border bg-background px-4 py-3'>
                                    <Sun className='mt-0.5 size-4 shrink-0 text-primary' />
                                    <span className='text-sm text-foreground'>
                                        <span className='block font-semibold'>
                                            {changeDate ? `${formatDay(changeDate)} and the days after` : 'The days after'}
                                        </span>
                                        <span className='mt-0.5 block text-muted-foreground'>
                                            {spring
                                                ? 'Get outside for some morning daylight, which helps set your body clock earlier.'
                                                : 'Get daylight in the afternoon and evening, which helps your body clock move later.'}{' '}
                                            Keep to your usual times. Sleep can stay unsettled for {CLOCK_EFFECT_DAYS} days, so aim
                                            for at least 7 hours a night and take extra care driving if you feel drowsy.
                                        </span>
                                    </span>
                                </span>
                            </span>
                        </div>
                    )}
                </div>

                <div className='mt-5 flex gap-2 rounded-xl border border-border bg-secondary/40 px-4 py-3'>
                    <Info className='mt-0.5 size-4 shrink-0 text-muted-foreground' />
                    <span className='text-xs leading-relaxed text-muted-foreground'>
                        Based on American Academy of Sleep Medicine advice. Dates are for the US and Canada or the UK and
                        Europe; other countries change on different dates or not at all. Nothing you enter is stored. For
                        bigger shifts, try the{' '}
                        <Link href='/sleep/jet-lag-planner' className='font-normal text-inherit underline underline-offset-2 hover:text-foreground'>
                            jet lag planner
                        </Link>
                        .
                    </span>
                </div>
            </div>
        </section>
    )
}

export default ClockChangePlanner
